import React, { useState, useEffect, useMemo } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Dropdown } from "../../../../components/Dropdown";
import { Button } from "../../../../components/Button";
import { apiGet, apiPost, apiPut } from "../../../../services/apiServices";
import { endPoints } from "../../../../configs/apiUrls";
import { toast } from "react-toastify";
import Switch from "../../../../components/Switch";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import indiaData from "../../../../data/stateData.json";
import Autocomplete from "react-google-autocomplete";
import ImageUploader from "../../../../components/ImageUploader";

// --- STATIC OPTIONS ---
const facingOptions = [
    { label: "North", value: "North" },
    { label: "South", value: "South" },
    { label: "East", value: "East" },
    { label: "West", value: "West" },
    { label: "North-East", value: "North-East" },
    { label: "North-West", value: "North-West" },
    { label: "South-East", value: "South-East" },
    { label: "South-West", value: "South-West" },
];

const AddPropertyForSale = () => {
    const { id } = useParams<{ id?: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useSelector((state: RootState) => state.auth);

    const isEditMode = Boolean(id);

    const [initialValues, setInitialValues] = useState({
        userId: "",
        remarks: "",
        area: "",
        street: "",
        areaSqFt: "",
        city: "",
        propertyType: "",
        state: "Karnataka",
        surveyNumber: "",
        propertyNumber: "", // 👈 Added propertyNumber field
        sellingPrice: "",
        address: "",
        lat: "",
        lng: "",
        isApproved: false,
        isActive: true,
        isGoogle: false,
        photos: [null, null, null],
        hasBuilding: false,
        bua: "",
        floors: "",
        facing: "",
    });

    const [areaOptions, setAreaOptions] = useState<any[]>([]);
    const [streetOptions, setStreetOptions] = useState<any[]>([]);
    const [propertyTypeOptions, setPropertyTypeOptions] = useState<any[]>([]);
    const [loadingData, setLoadingData] = useState<boolean>(true);
    
    // --- Gallery Preview State ---
    const [previewData, setPreviewData] = useState<{ urls: string[], currentIndex: number } | null>(null);

    const stateOptions = useMemo(
        () => indiaData.map((s) => ({ label: s.state, value: s.state })),
        []
    );

    const cityOptions = useMemo(
        () =>
            indiaData.flatMap((s) =>
                s.districts.map((d) => ({
                    label: d,
                    value: d,
                    state: s.state,
                }))
            ),
        []
    );

    const fetchPropertyTypes = async () => {
        try {
            const res: any = await apiGet(endPoints.PROPERTY_TYPE);
            if (res?.success) {
                const types = res.data.map((p: any) => ({
                    label: p.propertyType || p.name || p.label,
                    value: p._id || p.value,
                }));
                setPropertyTypeOptions(types);
                return types;
            }
        } catch (error) {
            // console.error("Error fetching property types:", error);
        }
        return [];
    };

    const getAreaList = async (city: string) => {
        try {
            const res: any = await apiGet(`${endPoints.GET_CITY_BY_STATE}/${city}`);
            const areas = res?.success
                ? res.data.map((a: any) => ({
                    label: a.name || a.areaName || a.area || a.label,
                    value: a._id || a.value,
                }))
                : [];
            setAreaOptions(areas);
            return areas;
        } catch (error) {
            console.error("Error fetching areas:", error);
            setAreaOptions([]);
            return [];
        }
    };

    const getStreetList = async (areaId: string) => {
        try {
            const res: any = await apiGet(`${endPoints.GET_STREET_BY_AREA}/${areaId}`);
            const streets = res?.success
                ? res.data.map((s: any) => ({
                    label: s.name || s.streetName || s.street || s.label,
                    value: s._id || s.value,
                }))
                : [];
            setStreetOptions(streets);
            return streets;
        } catch (error) {
            console.error("Error fetching streets:", error);
            setStreetOptions([]);
            return [];
        }
    };

    useEffect(() => {
        const initData = async () => {
            setLoadingData(true);
            try {
                await fetchPropertyTypes();

                if (isEditMode && location.state?.row) {
                    const row = location.state.row;

                    const city = row?.city || "";
                    const areaId = typeof row?.area === "object" ? row?.area?._id : row?.area || "";
                    const streetId = typeof row?.street === "object" ? row?.street?._id : row?.street || "";
                    const propertyTypeId = row?.propertyType?._id;

                    if (city) {
                        await getAreaList(city);
                    }
                    if (areaId) {
                        await getStreetList(areaId);
                    }

                    setInitialValues({
                        userId: row?.userId?._id || row?.userId || "",
                        remarks: row?.remarks || "",
                        area: areaId,
                        street: streetId,
                        areaSqFt: row?.areaSqFt ? String(row.areaSqFt) : "",
                        city: city,
                        propertyType: propertyTypeId,
                        state: row?.state || "Karnataka",
                        surveyNumber: row?.surveyNumber || "",
                        propertyNumber: row?.propertyNumber || "", // 👈 Populate on edit
                        sellingPrice: row?.sellingPrice ? String(row.sellingPrice) : "",
                        address: row?.location?.address || "",
                        lat: row?.location?.lat ? String(row.location.lat) : "",
                        lng: row?.location?.lng ? String(row.location.lng) : "",
                        isApproved: row?.isApproved || false,
                        isActive: row?.isActive ?? true,
                        isGoogle: row?.isGoogle ?? true,
                        photos:
                            row?.photos && Array.isArray(row.photos)
                                ? row.photos.slice(0, 3).concat(Array(Math.max(0, 3 - row.photos.length)).fill(null)).slice(0, 3)
                                : [null, null, null],
                        hasBuilding: row?.hasBuilding || false,
                        bua: row?.bua ? String(row.bua) : "",
                        floors: row?.floors ? String(row.floors) : "",
                        facing: row?.facing || "",
                    });
                }
            } catch (err) {
                console.error("Initialization error:", err);
            } finally {
                setLoadingData(false);
            }
        };

        initData();
    }, [isEditMode, location.state]);

    const validationSchema = Yup.object().shape({
        propertyType: Yup.string().required("Property type is required"),
        state: Yup.string().required("State is required"),
        city: Yup.string().required("City is required"),
        area: Yup.string().required("Area is required"),
        street: Yup.string().required("Street is required"),
        propertyNumber: Yup.string(), // 👈 Optional free-text
        surveyNumber: Yup.string(),
        areaSqFt: Yup.number().positive("Invalid size").required("Area size is required"),
        sellingPrice: Yup.number().positive("Invalid rate").required("Market rate is required"),
        address: Yup.string().required("Address is required"),

        hasBuilding: Yup.boolean(),
        bua: Yup.number().when("hasBuilding", {
            is: true,
            then: (schema) => schema.positive("Invalid BUA").required("BUA is required"),
            otherwise: (schema) => schema.notRequired(),
        }),
        floors: Yup.number().when("hasBuilding", {
            is: true,
            then: (schema) => schema.positive("Invalid floor count").required("Floors are required"),
            otherwise: (schema) => schema.notRequired(),
        }),
        facing: Yup.string().required("Facing is required")
    });
    console.log("user", user);


    const handleSubmit = async (values: typeof initialValues, { setSubmitting }: any) => {
        setSubmitting(true);
        try {
            let finalLat = values.lat;
            let finalLng = values.lng;

            if (!values.isGoogle) {
                const areaLabel = areaOptions.find((a) => a.value === values.area)?.label || "";
                const streetLabel = streetOptions.find((s) => s.value === values.street)?.label || "";
                
                const combinedAddress = `${values.address}, ${streetLabel}, ${areaLabel}, ${values.city}, ${values.state}`;
                
                try {
                    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
                    const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(combinedAddress)}&key=${apiKey}`);
                    const data = await response.json();
                    
                    if (data.results && data.results.length > 0) {
                        finalLat = data.results[0].geometry.location.lat;
                        finalLng = data.results[0].geometry.location.lng;
                    }
                } catch (geoError) {
                    console.error("Geocoding fallback failed", geoError);
                }
            }

            const formData = new FormData();
            const targetUserId = isEditMode ? values.userId : user?._id;

            if (targetUserId) {
                formData.append("userId", targetUserId);
            }

            formData.append("state", values.state || "");
            formData.append("city", values.city || "");
            formData.append("area", values.area || "");
            formData.append("street", values.street || "");
            formData.append("propertyType", values.propertyType || "");
            formData.append("propertyNumber", values.propertyNumber || ""); // 👈 Append propertyNumber
            formData.append("surveyNumber", values.surveyNumber || "");
            formData.append("areaSqFt", values.areaSqFt ? String(values.areaSqFt) : "");
            formData.append("sellingPrice", values.sellingPrice ? String(values.sellingPrice) : "");
            formData.append("remarks", values.remarks || "");

            formData.append("location[address]", values.address || "");
            formData.append("location[lat]", finalLat ? String(finalLat) : "0");
            formData.append("location[lng]", finalLng ? String(finalLng) : "0");
            
            formData.append("isGoogle", String(values.isGoogle));
            formData.append("isApproved", String(values.isApproved));
            formData.append("isActive", String(values.isActive));
            formData.append("facing", values.facing || "");

            formData.append("hasBuilding", values.hasBuilding ? "true" : "false");
            if (values.hasBuilding) {
                formData.append("bua", values.bua ? String(values.bua) : "");
                formData.append("floors", values.floors ? String(values.floors) : "");
            }

            let hasAnyImage = false;

            values.photos.forEach((img: any) => {
                if (!img) return;

                hasAnyImage = true;

                if (img instanceof File || img instanceof Blob) {
                    formData.append("photos", img);
                } else if (typeof img === "string") {
                    try {
                        const urlWithoutQuery = img.split("?")[0];
                        let s3Key = "";

                        if (urlWithoutQuery.includes(".amazonaws.com/")) {
                            s3Key = urlWithoutQuery.split(".amazonaws.com/")[1];
                        } else if (urlWithoutQuery.includes("properties/")) {
                            s3Key = "properties/" + urlWithoutQuery.split("properties/")[1];
                        } else {
                            s3Key = urlWithoutQuery;
                        }

                        if (s3Key) {
                            formData.append("existingPhotos", decodeURIComponent(s3Key));
                        }
                    } catch (e) {
                        console.error("Failed to extract S3 key from URL", e);
                    }
                } else if (img?.file instanceof File) {
                    formData.append("photos", img.file);
                }
            });

            if (!hasAnyImage) {
                formData.append("existingPhotos", "EMPTY_ARRAY");
            }

            const headers = { "Content-Type": "multipart/form-data" };

            const res = isEditMode
                ? await apiPut(`${endPoints.PROPERTIES_FOR_SALE}/${id}`, formData, headers)
                : await apiPost(endPoints.PROPERTIES_FOR_SALE, formData, headers);

            if (res?.success) {
                toast.success(`Property ${isEditMode ? "updated" : "created"} successfully!`);
                navigate(-1);
            } else {
                toast.error(res?.data?.message || res?.message || "Operation failed");
            }
        } catch (error: any) {
            toast.error(error?.data?.message || error?.message || "Something went wrong");
        } finally {
            setSubmitting(false);
        }
    };

    const handleOpenGallery = (photos: any[], clickedIndex: number) => {
        const validPhotos = photos.map((img, idx) => ({ img, originalIndex: idx })).filter(p => p.img !== null);
        
        const urls = validPhotos.map(p => {
            const img = p.img;
            if (typeof img === "string") return img;
            if (img instanceof File || img instanceof Blob) return URL.createObjectURL(img);
            if (img?.file instanceof File) return URL.createObjectURL(img.file);
            return "";
        });

        const relativeIndex = validPhotos.findIndex(p => p.originalIndex === clickedIndex);

        if (urls.length > 0) {
            setPreviewData({ urls, currentIndex: relativeIndex > -1 ? relativeIndex : 0 });
        }
    };

    if (loadingData) {
        return (
            <div className="max-w-7xl mx-auto p-12 flex justify-center items-center">
                <div className="text-subTitleText font-medium">Loading details...</div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto p-6 bg-cardBackground rounded shadow relative">
            <h2 className="text-2xl font-semibold mb-6 text-headingText">
                {isEditMode ? "Edit Property" : "Add New Property"}
            </h2>

            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ values, setFieldValue, isSubmitting }) => {
                    const filteredCityOptions = cityOptions.filter(
                        (c) => c.state === values.state
                    );

                    return (
                        <Form className="grid grid-cols-1 md:grid-cols-3 gap-10">
                            {/* Property Type */}
                            <div className="w-full">
                                <label className="block text-sm font-medium text-subTitleText mb-1">
                                    Property Type <span className="text-errorText">*</span>
                                </label>
                                <Dropdown
                                    options={propertyTypeOptions}
                                    value={values.propertyType}
                                    onChange={(val: string) => setFieldValue("propertyType", val)}
                                    placeholder="Select property type"
                                    className="w-full"
                                />
                                <ErrorMessage name="propertyType" component="div" className="mt-1 text-errorText text-sm" />
                            </div>

                            {/* State */}
                            <div>
                                <label className="block text-sm font-medium text-subTitleText mb-1">
                                    State <span className="text-errorText">*</span>
                                </label>
                                <Dropdown
                                    options={stateOptions}
                                    value={values.state || ""}
                                    onChange={(val: string) => {
                                        setFieldValue("state", val);
                                        setFieldValue("city", "");
                                        setFieldValue("area", "");
                                        setFieldValue("street", "");
                                        setAreaOptions([]);
                                        setStreetOptions([]);
                                    }}
                                    placeholder="Select state"
                                    className="w-full"
                                />
                                <ErrorMessage name="state" component="div" className="mt-1 text-errorText text-sm" />
                            </div>

                            {/* City */}
                            <div>
                                <label className="block text-sm font-medium text-subTitleText mb-1">
                                    City <span className="text-errorText">*</span>
                                </label>
                                <Dropdown
                                    options={filteredCityOptions}
                                    value={values.city || ""}
                                    onChange={(val: string) => {
                                        setFieldValue("city", val);
                                        setFieldValue("area", "");
                                        setFieldValue("street", "");
                                        setStreetOptions([]);
                                        if (val) getAreaList(val);
                                    }}
                                    placeholder="Select city"
                                    disabled={!values.state}
                                    className="w-full"
                                />
                                <ErrorMessage name="city" component="div" className="mt-1 text-errorText text-sm" />
                            </div>

                            {/* Area */}
                            <div>
                                <label className="block text-sm font-medium text-subTitleText mb-1">
                                    Area <span className="text-errorText">*</span>
                                </label>
                                <Dropdown
                                    options={areaOptions}
                                    value={values.area}
                                    onChange={(val: string) => {
                                        setFieldValue("area", val);
                                        setFieldValue("street", "");
                                        if (val) getStreetList(val);
                                    }}
                                    placeholder="Select area"
                                    disabled={!values.city}
                                    className="w-full"
                                />
                                <ErrorMessage name="area" component="div" className="mt-1 text-errorText text-sm" />
                            </div>

                            {/* Street */}
                            <div>
                                <label className="block text-sm font-medium text-subTitleText mb-1">
                                    Street <span className="text-errorText">*</span>
                                </label>
                                <Dropdown
                                    options={streetOptions}
                                    value={values.street}
                                    onChange={(val: string) => setFieldValue("street", val)}
                                    placeholder="Select street"
                                    disabled={!values.area}
                                    className="w-full"
                                />
                                <ErrorMessage name="street" component="div" className="mt-1 text-errorText text-sm" />
                            </div>

                            {/* Property Number (Free Text) */}
                            <div>
                                <label className="block text-sm font-medium text-subTitleText mb-1">
                                    Property Number
                                </label>
                                <Field
                                    type="text"
                                    name="propertyNumber"
                                    placeholder="Enter property number (e.g. #12/A, Plot 45)"
                                    className="w-full border border-borderColor rounded px-3 py-2"
                                />
                                <ErrorMessage name="propertyNumber" component="div" className="mt-1 text-errorText text-sm" />
                            </div>

                            {/* Survey Number */}
                            <div>
                                <label className="block text-sm font-medium text-subTitleText mb-1">
                                    Survey Number
                                </label>
                                <Field
                                    type="text"
                                    name="surveyNumber"
                                    placeholder="Enter survey number"
                                    className="w-full border border-borderColor rounded px-3 py-2"
                                />
                                <ErrorMessage name="surveyNumber" component="div" className="mt-1 text-errorText text-sm" />
                            </div>

                            {/* Area SqFt */}
                            <div>
                                <label className="block text-sm font-medium text-subTitleText mb-1">
                                    Area SqFt <span className="text-errorText">*</span>
                                </label>
                                <Field
                                    type="number"
                                    name="areaSqFt"
                                    placeholder="Enter area size in sq ft"
                                    className="w-full border border-borderColor rounded px-3 py-2"
                                />
                                <ErrorMessage name="areaSqFt" component="div" className="mt-1 text-errorText text-sm" />
                            </div>

                            {/* Selling Price */}
                            <div>
                                <label className="block text-sm font-medium text-subTitleText mb-1">
                                    Selling Price <span className="text-errorText">*</span>
                                </label>
                                <Field
                                    type="number"
                                    name="sellingPrice"
                                    placeholder="Enter market rate"
                                    className="w-full border border-borderColor rounded px-3 py-2"
                                />
                                <ErrorMessage name="sellingPrice" component="div" className="mt-1 text-errorText text-sm" />
                            </div>

                            {/* Google Map Address / Manual Input */}
                            <div className="md:col-span-1">
                                <label className="block text-sm font-medium text-subTitleText mb-1">
                                    Address <span className="text-errorText">*</span>
                                </label>
                                <Autocomplete
                                    apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                                    onChange={(e: any) => {
                                        setFieldValue("address", e.target.value);
                                        setFieldValue("isGoogle", false);
                                    }}
                                    onPlaceSelected={(place: any) => {
                                        if (place.geometry) {
                                            const lat = place.geometry.location.lat();
                                            const lng = place.geometry.location.lng();

                                            setFieldValue("address", place.formatted_address);
                                            setFieldValue("lat", lat);
                                            setFieldValue("lng", lng);
                                            setFieldValue("isGoogle", true);
                                        }
                                    }}
                                    options={{
                                        types: ["geocode", "establishment"],
                                        componentRestrictions: { country: "in" },
                                    }}
                                    defaultValue={values.address}
                                    placeholder="Search or enter address manually"
                                    className="w-full border border-borderColor rounded px-3 py-2"
                                />
                                <ErrorMessage name="address" component="div" className="mt-1 text-errorText text-sm" />
                            </div>

                            {/* Facing */}
                            <div>
                                <label className="block text-sm font-medium text-subTitleText mb-1">
                                    Facing <span className="text-errorText">*</span>
                                </label>
                                <Dropdown
                                    options={facingOptions}
                                    value={values.facing}
                                    onChange={(val: string) => setFieldValue("facing", val)}
                                    placeholder="Select Facing"
                                    className="w-full"
                                />
                                <ErrorMessage name="facing" component="div" className="mt-1 text-errorText text-sm" />
                            </div>

                            {/* ---------------- BUILDING DETAILS SECTION ---------------- */}
                            <div className="md:col-span-3 mt-4 pt-4 border-t border-borderColor">
                                <div className="flex items-center gap-4 mb-4">
                                    <label className="text-base font-semibold text-headingText">
                                        Having Building?
                                    </label>
                                    <Switch
                                        checked={values.hasBuilding}
                                        onCheckedChange={(val: boolean) => setFieldValue("hasBuilding", val)}
                                    />
                                </div>

                                {values.hasBuilding && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                                        <div>
                                            <label className="block text-sm font-medium text-subTitleText mb-1">
                                                BUA / Super BUA (SqFt) <span className="text-errorText">*</span>
                                            </label>
                                            <Field
                                                type="number"
                                                name="bua"
                                                placeholder="Enter BUA"
                                                className="w-full border border-borderColor rounded px-3 py-2"
                                            />
                                            <ErrorMessage name="bua" component="div" className="mt-1 text-errorText text-sm" />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-subTitleText mb-1">
                                                Floors / Floor No <span className="text-errorText">*</span>
                                            </label>
                                            <Field
                                                type="number"
                                                name="floors"
                                                placeholder="No. of Floors"
                                                className="w-full border border-borderColor rounded px-3 py-2"
                                            />
                                            <ErrorMessage name="floors" component="div" className="mt-1 text-errorText text-sm" />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Remarks */}
                            <div className="md:col-span-3 mt-2">
                                <label className="block text-sm font-medium text-subTitleText mb-1">
                                    Remarks
                                </label>
                                <Field
                                    as="textarea"
                                    name="remarks"
                                    placeholder="Enter remarks"
                                    className="w-full border border-borderColor rounded px-3 py-2 min-h-[100px]"
                                />
                            </div>

                            {/* ---------- IMAGES UPLOAD ---------- */}
                            <div className="md:col-span-3">
                                <label className="block text-sm font-medium text-subTitleText mb-1">
                                    Property Images (Optional)
                                </label>
                                <div className="flex gap-6 mt-2">
                                    {values.photos.map((img: any, index: number) => (
                                        <div key={index} className="flex flex-col items-center gap-2">
                                            <ImageUploader
                                                value={img}
                                                onImageSelected={(image) => {
                                                    const updatedImages: any = [...values.photos];
                                                    updatedImages[index] = image;
                                                    setFieldValue("photos", updatedImages);
                                                }}
                                                onImageClear={() => {
                                                    const updatedImages = [...values.photos];
                                                    updatedImages[index] = null;
                                                    setFieldValue("photos", updatedImages);
                                                }}
                                            />
                                            {img && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleOpenGallery(values.photos, index);
                                                    }}
                                                    className="text-xs font-semibold text-primary hover:underline bg-gray-100 px-3 py-1 rounded"
                                                >
                                                    View Image
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Approve + Active */}
                            <div className="flex items-center gap-6 mt-4">
                                <div className="flex items-center gap-2">
                                    <label className="text-sm font-medium text-subTitleText">Approve</label>
                                    <Switch
                                        checked={values.isApproved}
                                        onCheckedChange={(val: boolean) => setFieldValue("isApproved", val)}
                                    />
                                </div>
                                <div className="flex items-center gap-2">
                                    <label className="text-sm font-medium text-subTitleText">Active</label>
                                    <Switch
                                        checked={values.isActive}
                                        onCheckedChange={(val: boolean) => setFieldValue("isActive", val)}
                                    />
                                </div>
                            </div>

                            {/* Submit */}
                            <div className="md:col-span-3 pt-4">
                                <Button loading={isSubmitting} variant="primary" type="submit">
                                    {isEditMode ? "Update Property" : "Create Property"}
                                </Button>
                            </div>
                        </Form>
                    );
                }}
            </Formik>

            {/* 🚨 FULL-SCREEN IMAGE GALLERY MODAL */}
            {previewData && previewData.urls.length > 0 && (
                <div 
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4"
                    onClick={() => setPreviewData(null)}
                >
                    {/* Close Button */}
                    <button 
                        className="absolute top-6 right-8 text-white text-5xl font-bold hover:text-gray-300 z-[60] focus:outline-none"
                        onClick={() => setPreviewData(null)}
                        title="Close"
                    >
                        &times;
                    </button>
                    
                    {/* Previous Button */}
                    {previewData.urls.length > 1 && (
                        <button 
                            className="absolute left-4 md:left-12 text-white text-6xl hover:text-gray-300 z-[60] focus:outline-none"
                            onClick={(e) => {
                                e.stopPropagation();
                                setPreviewData(prev => prev ? { 
                                    ...prev, 
                                    currentIndex: (prev.currentIndex - 1 + prev.urls.length) % prev.urls.length 
                                } : null);
                            }}
                        >
                            &#8249;
                        </button>
                    )}

                    {/* Image Container */}
                    <div 
                        className="relative max-w-5xl flex items-center justify-center pointer-events-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img 
                            src={previewData.urls[previewData.currentIndex]} 
                            alt={`Preview ${previewData.currentIndex + 1}`} 
                            className="max-w-full max-h-[85vh] object-contain rounded bg-white p-2 shadow-2xl" 
                        />
                        
                        {/* Image Counter Badge */}
                        {previewData.urls.length > 1 && (
                            <div className="absolute -bottom-10 text-white font-medium bg-black bg-opacity-50 px-4 py-1 rounded-full">
                                {previewData.currentIndex + 1} / {previewData.urls.length}
                            </div>
                        )}
                    </div>

                    {/* Next Button */}
                    {previewData.urls.length > 1 && (
                        <button 
                            className="absolute right-4 md:right-12 text-white text-6xl hover:text-gray-300 z-[60] focus:outline-none"
                            onClick={(e) => {
                                e.stopPropagation();
                                setPreviewData(prev => prev ? { 
                                    ...prev, 
                                    currentIndex: (prev.currentIndex + 1) % prev.urls.length 
                                } : null);
                            }}
                        >
                            &#8250;
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default AddPropertyForSale;